<script setup>
import { computed } from 'vue';

import { HrDivider, HrItemLabel } from '../primitives';
import HrMenuItem from './HrMenuItem.vue';

/**
 * Data-driven menu content — renders a flat or grouped list of HrMenuItem rows from an options array.
 *
 * @status recommended
 * @group actions
 * @useWhen Populating a menu from a data array rather than composing HrMenuItem rows by hand
 * @avoidWhen You need custom slot content per item — compose HrMenuItem rows directly instead
 * @related HrMenuItem
 * @related HrOverflowMenuBtn
 */
defineOptions({ name: 'HrActionMenu' });

/**
 * @typedef {{ label: string, value?: *, icon?: string, trailingText?: string,
 *   disable?: boolean, destructive?: boolean, action?: () => void }}
 *   HrActionMenuOption
 * @typedef {{ group: string, options: HrActionMenuOption[] }} HrActionMenuGroup
 * @typedef {{ subHeader: string, options: HrActionMenuOption[] }} HrActionMenuSubHeaderGroup
 * @typedef {HrActionMenuOption | HrActionMenuGroup | HrActionMenuSubHeaderGroup} HrActionMenuEntry
 */

const props = defineProps({
  /**
   * Flat or grouped option list. Each entry is either:
   *   - An option:       { label, value?, icon?, trailingText?, disable?, destructive?, action? }
   *   - A group:         { group, options: [...] } — separated by a divider, no label rendered
   *   - A subHeader:     { subHeader, options: [...] } — renders a visible section header
   * `action` is called on click in addition to the `select` event. Cannot be expressed as JSON.
   * Order the entries as HrMenuItem documents: the primary action first, anything `destructive`
   * last and in a group of its own.
   * @type {HrActionMenuEntry[]}
   * @example [
   *   {"label":"Copy","icon":"content_copy","trailingText":"⌘C"},
   *   {"label":"Move","icon":"drive_file_move"},
   *   {"group":"destructive","options":[
   *     {"label":"Rename","disable":true},
   *     {"label":"Delete","icon":"delete","destructive":true}
   *   ]},
   *   {"subHeader":"Export","options":[
   *     {"label":"As CSV","trailingText":"⌘E"},
   *     {"label":"As PDF"}
   *   ]}
   * ]
   */
  options: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits({
  /** Fired when the user selects an option; payload is the selected HrActionMenuOption object. */
  select: null,
});

const DIVIDER = Object.freeze({ _divider: true });

const flattenedOptions = computed(() => {
  const result = [];
  let lastWasGroup = false;

  props.options.forEach((entry) => {
    if ('subHeader' in entry) {
      result.push({ _header: true, label: entry.subHeader });
      result.push(...entry.options);
      lastWasGroup = true;
    } else if ('group' in entry) {
      if (result.length > 0) {
        result.push(DIVIDER);
      }
      result.push(...entry.options);
      lastWasGroup = true;
    } else {
      if (lastWasGroup && result.length > 0) {
        result.push(DIVIDER);
      }
      result.push(entry);
      lastWasGroup = false;
    }
  });

  /*
   * A destructive entry is always held off from what sits above it, whether or not the caller
   * grouped its options. Without this a flat list puts Delete directly under the entry a reader
   * aims for most, and the one press that cannot be taken back is the easiest to hit by mistake.
   */
  const firstDestructive = result.findIndex((item) => item.destructive);

  if (firstDestructive > 0 && result[firstDestructive - 1] !== DIVIDER) {
    result.splice(firstDestructive, 0, DIVIDER);
  }

  return result;
});
</script>

<template>
  <template
    v-for="(item, index) in flattenedOptions"
    :key="index"
  >
    <hr-divider v-if="item._divider" />
    <hr-item-label
      v-else-if="item._header"
      header
    >
      {{ item.label }}
    </hr-item-label>
    <hr-menu-item
      v-else
      v-close-popup
      :label="item.label ?? null"
      :icon="item.icon ?? null"
      :trailing-text="item.trailingText ?? null"
      :disable="item.disable ?? false"
      :destructive="item.destructive ?? false"
      @click="item.action?.(); emit('select', item)"
    />
  </template>
</template>
